import { Field, Input, Label } from '@headlessui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { FormProvider, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { Link } from 'react-router-dom'
import { object, string } from 'yup'
import {
    ContentFeedCreate,
    ContentFeedUpdate,
    ContentFeedView,
    useCreateContentFeedMutation,
    useUpdateContentFeedMutation
} from '../../api/client'
import Button from '../Button'
import SearchParameters, {
    mapToContentSearchParams
} from '../content/search/searchParameters'

interface FormData {
    description?: string
    name: string
}

type Props = {
    onCompleted: (newContentFeedId: number, isNew: boolean) => void
    searchParameters: SearchParameters
    contentFeed?: ContentFeedView
}

const validationSchema = object({
    name: string().required('Name is required'),
    description: string().optional()
})

/**
 * Modal that allows the user to remove content from the spam model
 */
export default function ContentFeedEditForm({ onCompleted, contentFeed, searchParameters }: Props) {
    const [createContentFeedMutation, { loading: createLoading }] = useCreateContentFeedMutation()

    const [updateContentFeedMutation, { loading: updateLoading }] = useUpdateContentFeedMutation()

    const form = useForm<FormData>({
        mode: 'onChange',
        resolver: yupResolver(validationSchema)
    })

    const onSave = async (data: FormData) => {
        // We're going to map between models here, because it's common for update and create
        const mappedSearchParameters = mapToContentSearchParams(searchParameters)

        if (contentFeed) {
            const contentFeedUpdate: ContentFeedUpdate = {
                id: contentFeed.id,
                displayName: data.name,
                description: data.description,
                searchParameters: mappedSearchParameters
            }
            updateContentFeedMutation({
                variables: {
                    contentFeedUpdate
                },
                onCompleted: (data) => {
                    onCompleted(data.updateContentFeed.id, false)
                },
                onError: (error) => {
                    toast.error(`Failed to update content feed: ${error}`)
                }
            })
        }
    }

    const onSaveAsNew = async (data: FormData) => {
        const mappedSearchParameters = mapToContentSearchParams(searchParameters)

        const contentFeedNew: ContentFeedCreate = {
            displayName: data.name,
            description: data.description,
            searchParameters: mappedSearchParameters
        }
        createContentFeedMutation({
            variables: { contentFeedNew },
            onCompleted: (data) => {
                return onCompleted(data.createContentFeed.id, true)
            },
            onError: (error) => {
                return toast.error(`Failed to create content feed: ${error}`)
            }
        })
    }

    return (
        <div className="mb-3 flex flex-col">
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(onSave)}>
                    <p className="text-sm text-ink-600">
                        Once saved this will appear on your{' '}
                        <Link to="/" className="text-arwen-700 underline hover:text-arwen-800">
                            homepage
                        </Link>{' '}
                        with the following name.
                    </p>
                    <Field className="mt-6">
                        <Label className="block leading-6 text-ink-700">Name</Label>
                        <Input
                            type="text"
                            defaultValue={contentFeed?.displayName}
                            className="block w-full rounded-md border-0 py-1.5 text-ink-900 shadow ring-1 ring-inset ring-ink-200 placeholder:text-ink-400 focus:ring-2 focus:ring-inset focus:ring-arwen-500 sm:leading-6"
                            placeholder="A short name"
                            {...form.register('name')}
                        />
                        {form.formState.errors.name && (
                            <span className="text-sm text-arwen-600">
                                {form.formState.errors.name.message}
                            </span>
                        )}
                    </Field>

                    <Field className="mt-6">
                        <Label className="block leading-6 text-ink-700">Description</Label>
                        <Input
                            defaultValue={contentFeed?.description ?? ''}
                            className="block w-full rounded-md border-0 py-1.5 text-ink-900 shadow ring-1 ring-inset ring-ink-200 placeholder:text-ink-400 focus:ring-2 focus:ring-inset focus:ring-arwen-500 sm:leading-6"
                            placeholder="A longer description of the report and its purpose."
                            {...form.register('description')}
                        />
                        {form.formState.errors.description && (
                            <span className="text-sm text-arwen-600">
                                {form.formState.errors.description.message}
                            </span>
                        )}
                    </Field>
                    <div className="mt-10 space-x-4">
                        {contentFeed ? (
                            <Button
                                primary
                                text={'Save'}
                                type="submit"
                                size="large"
                                disabled={createLoading || updateLoading}
                                loading={updateLoading ?? false}
                            />
                        ) : undefined}
                        <Button
                            primary
                            text="Save as new content feed"
                            type="button"
                            size="large"
                            onClick={form.handleSubmit(onSaveAsNew)}
                            disabled={createLoading || updateLoading}
                            loading={createLoading ?? false}
                        />
                    </div>
                </form>
            </FormProvider>
        </div>
    )
}
